import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { Skeleton } from '@/components/ui/Skeleton';
import { ToastProvider } from '@/components/ui/Toast';
import { AdminRoute } from '@/components/admin/AdminRoute';
import { SuperAdminRoute } from '@/components/admin/SuperAdminRoute';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { useAppStore } from '@/stores/appStore';

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const TopUpPage = lazy(() => import('@/pages/TopUpPage'));
const TransactionHistoryPage = lazy(() => import('@/pages/TransactionHistoryPage'));
const UsagePage = lazy(() => import('@/pages/UsagePage'));
const HomePage = lazy(() => import('@/pages/HomePage'));
const GenerationPage = lazy(() => import('@/pages/GenerationPage'));
const GenerationHistoryPage = lazy(() => import('@/pages/GenerationHistoryPage'));
const AssetsPage = lazy(() => import('@/pages/AssetsPage'));
const AssetDetailPage = lazy(() => import('@/pages/AssetDetailPage'));
const PortraitGalleryPage = lazy(() => import('@/pages/PortraitGalleryPage'));
const PortraitUploadPage = lazy(() => import('@/pages/PortraitUploadPage'));
const ToolsPage = lazy(() => import('@/pages/ToolsPage'));
const BatchPage = lazy(() => import('@/pages/BatchPage'));
const StoryDramaPage = lazy(() => import('@/pages/StoryDramaPage'));
const DigitalHumanPage = lazy(() => import('@/pages/DigitalHumanPage'));
const CopywritingPage = lazy(() => import('@/pages/CopywritingPage'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminFeatures = lazy(() => import('@/pages/admin/AdminFeatures'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminPointsReport = lazy(() => import('@/pages/admin/AdminPointsReport'));

function Loading() {
  return <div className="flex items-center justify-center min-h-screen"><Skeleton className="w-64 h-8" /></div>;
}

function RootRedirect() {
  const hostname = window.location.hostname;
  if (hostname.startsWith('admin.')) {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/home" replace />;
}

export default function App() {
  const theme = useAppStore((s) => s.theme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  return (
    <BrowserRouter>
      <ToastProvider>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route element={<ProtectedRoute />}>
              <Route element={<AppShell />}>
                <Route path="/home" element={<HomePage />} />
                <Route path="/generate" element={<GenerationHistoryPage />} />
                <Route path="/generate/:taskId" element={<GenerationPage />} />
                <Route path="/assets" element={<AssetsPage />} />
                <Route path="/assets/:assetId" element={<AssetDetailPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/profile/topup" element={<TopUpPage />} />
                <Route path="/profile/transactions" element={<TransactionHistoryPage />} />
                <Route path="/topup" element={<TopUpPage />} />
                <Route path="/usage" element={<UsagePage />} />
                <Route path="/portraits" element={<PortraitGalleryPage />} />
                <Route path="/portraits/new" element={<PortraitUploadPage />} />
                <Route path="/portraits/upload" element={<PortraitUploadPage />} />
                <Route path="/portraits/:portraitId/upload" element={<PortraitUploadPage />} />
                <Route path="/tools" element={<ToolsPage />} />
                <Route path="/batch" element={<BatchPage />} />
                <Route path="/story-drama" element={<StoryDramaPage />} />
                <Route path="/digital-human" element={<DigitalHumanPage />} />
                <Route path="/copywriting" element={<CopywritingPage />} />
              </Route>
            </Route>
            <Route path="/admin" element={<AdminRoute />}>
              <Route element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="features" element={<AdminFeatures />} />
                <Route path="users" element={<AdminUsers />} />
                <Route element={<SuperAdminRoute />}>
                  <Route path="points-report" element={<AdminPointsReport />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/profile" replace />} />
          </Routes>
        </Suspense>
      </ToastProvider>
    </BrowserRouter>
  );
}
